import React, { useEffect, useCallback, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { selectors } from "reducers";
import {
  fetchSynthpopPendingSummary,
  fetchPatientPendingStatus,
  clearPatientPendingStatus
} from "actions/synthpop-call-logs";
import { message, errorMessage } from "actions/message";
import { SumBlock, Waiting } from "components/ui";
import HasRolePermission from "components/shared/has-role-permission";
import { get } from "utils/api";
import moment from "moment";
import MdRefresh from "react-icons/lib/md/refresh";
import FaSearch from "react-icons/lib/fa/search";
import FaRefresh from "react-icons/lib/fa/refresh";
import FaExternalLink from "react-icons/lib/fa/external-link";

const formatDate = date =>
  date ? moment(date).format("M/D/YY h:mma") : "";

const sortByCountDesc = (a, b) => (b.count || 0) - (a.count || 0);

const SynthpopPendingCalls = ({ startDate, endDate }) => {
  const dispatch = useDispatch();
  const summary = useSelector(selectors.getSynthpopPendingSummary);
  const isFetching = useSelector(selectors.getIsFetchingSynthpopPendingSummary);
  const patientPending = useSelector(selectors.getSynthpopPatientPending);
  const isLookingUp = useSelector(selectors.getIsFetchingSynthpopPatientPending);

  const [lookupInput, setLookupInput] = useState("");
  const [processingUUID, setProcessingUUID] = useState(null);

  const fetchSummary = useCallback(() => {
    const isoStart = moment(startDate, "MM/DD/YYYY").format("YYYY-MM-DD");
    const isoEnd = moment(endDate, "MM/DD/YYYY").format("YYYY-MM-DD");
    dispatch(fetchSynthpopPendingSummary({ startDate: isoStart, endDate: isoEnd }));
  }, [dispatch, startDate, endDate]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const handleLookup = (e) => {
    e.preventDefault();
    const guid = lookupInput.trim();
    if (!guid) return;
    dispatch(fetchPatientPendingStatus(guid));
  };

  const handleClearLookup = () => {
    setLookupInput("");
    dispatch(clearPatientPendingStatus());
  };

  const handleCheckStatus = async (uuid) => {
    setProcessingUUID(uuid);
    try {
      const result = await get(`synthpop/fetch-task-details/${uuid}/true`);
      dispatch(message(result.message || "Status checked"));
      // Refresh both: summary (count may drop) and patient lookup (row may move off pending)
      fetchSummary();
      if (patientPending?.patientGuid) {
        dispatch(fetchPatientPendingStatus(patientPending.patientGuid));
      }
    } catch (err) {
      dispatch(errorMessage("Failed to check Synthpop status"));
    } finally {
      setProcessingUUID(null);
    }
  };

  const totalCount = summary?.totalCount ?? 0;
  const last24Hours = summary?.last24Hours ?? 0;
  // Sort defensively — backend may already sort, but we don't rely on it.
  const companyBreakdown = [...(summary?.companyBreakdown ?? [])].sort(sortByCountDesc);

  return (
    <div style={{
      padding: "10px",
      display: "flex",
      flexDirection: "column",
      gap: "20px"
    }}>
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: "15px", marginBottom: "10px" }}>
          <SumBlock
            title="Pending Callbacks"
            value={totalCount}
            subLabel={`Last 24h: ${last24Hours}`}
          />
          <button onClick={fetchSummary} title="Refresh" style={{ padding: "5px 10px", marginLeft: "auto" }}>
            <MdRefresh />
          </button>
        </div>
        <div style={{ color: "#666", fontSize: "13px", fontStyle: "italic" }}>
          Patients that are queued in Synthpop&apos;s system but haven&apos;t been called yet.
          Use the lookup below to check a specific patient or to force a status check.
        </div>
      </div>

      <PatientLookup
        lookupInput={lookupInput}
        setLookupInput={setLookupInput}
        onLookup={handleLookup}
        onClear={handleClearLookup}
        isLookingUp={isLookingUp}
        patientPending={patientPending}
        processingUUID={processingUUID}
        onCheckStatus={handleCheckStatus}
      />

      {isFetching ? (
        <Waiting reason="Loading pending summary..." />
      ) : (
        <CompanyBreakdownTable companies={companyBreakdown} />
      )}
    </div>
  );
};

SynthpopPendingCalls.propTypes = {
  startDate: PropTypes.string,
  endDate: PropTypes.string,
};

const CompanyBreakdownTable = ({ companies }) => {
  const [expanded, setExpanded] = useState(new Set());

  const toggle = (guid) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(guid)) next.delete(guid);
      else next.add(guid);
      return next;
    });
  };

  return (
    <div>
      <h4 style={{ marginTop: 0, marginBottom: "8px" }}>Pending by Company</h4>
      <table className="table" style={{ width: "100%", borderCollapse: "collapse", margin: 0 }}>
        <thead>
          <tr>
            <th style={{ backgroundColor: "#fff", width: "30px" }} />
            <th style={{ backgroundColor: "#fff" }}>Company</th>
            <th style={{ backgroundColor: "#fff", textAlign: "right", width: "120px" }}>Pending</th>
          </tr>
        </thead>
        <tbody>
          {companies.length === 0 ? (
            <tr>
              <td colSpan="3" style={{ textAlign: "center", padding: "12px", color: "#999" }}>
                No data.
              </td>
            </tr>
          ) : (
            companies.map(company => {
              const hasBranches = company.branches && company.branches.length > 0;
              const isExpanded = expanded.has(company.guid);
              const link = company.guid ? `/companies/${company.guid}/profile` : null;
              return (
                <React.Fragment key={company.guid || company.name}>
                  <tr
                    onClick={hasBranches ? () => toggle(company.guid) : undefined}
                    style={{ cursor: hasBranches ? "pointer" : "default" }}
                  >
                    <td style={{ textAlign: "center", color: "#666", userSelect: "none" }}>
                      {hasBranches ? (isExpanded ? "▼" : "▶") : ""}
                    </td>
                    <td>
                      {link ? (
                        <Link to={link} onClick={e => e.stopPropagation()}>
                          {company.name || "(unnamed)"}
                        </Link>
                      ) : (
                        company.name || "(unnamed)"
                      )}
                    </td>
                    <td style={{ textAlign: "right", fontWeight: "bold" }}>{company.count}</td>
                  </tr>
                  {isExpanded && hasBranches && [...company.branches].sort(sortByCountDesc).map(branch => (
                    <tr
                      key={`${company.guid}-${branch.guid || branch.name}`}
                      style={{ backgroundColor: "#fafafa" }}
                    >
                      <td />
                      <td style={{ paddingLeft: "30px", color: "#555" }}>
                        {branch.name || "(unnamed)"}
                      </td>
                      <td style={{ textAlign: "right" }}>{branch.count}</td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

CompanyBreakdownTable.propTypes = {
  companies: PropTypes.array.isRequired,
};

const PatientLookup = ({
  lookupInput,
  setLookupInput,
  onLookup,
  onClear,
  isLookingUp,
  patientPending,
  processingUUID,
  onCheckStatus
}) => (
  <div style={{ borderTop: "1px solid #eee", paddingTop: "15px" }}>
    <h4 style={{ marginTop: 0 }}>Look up a patient</h4>
    <form onSubmit={onLookup} style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "12px" }}>
      <input
        type="text"
        value={lookupInput}
        onChange={e => setLookupInput(e.target.value)}
        placeholder="Patient GUID"
        style={{ flex: 1, maxWidth: "400px", padding: "5px 8px" }}
      />
      <button type="submit" disabled={!lookupInput.trim() || isLookingUp} style={{ padding: "5px 12px" }}>
        <FaSearch /> {isLookingUp ? "Looking up..." : "Look up"}
      </button>
      {patientPending && (
        <button type="button" onClick={onClear} style={{ padding: "5px 12px" }}>
          Clear
        </button>
      )}
    </form>

    {patientPending && (
      <PatientLookupResult
        patientPending={patientPending}
        processingUUID={processingUUID}
        onCheckStatus={onCheckStatus}
      />
    )}
  </div>
);

PatientLookup.propTypes = {
  lookupInput: PropTypes.string.isRequired,
  setLookupInput: PropTypes.func.isRequired,
  onLookup: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  isLookingUp: PropTypes.bool,
  patientPending: PropTypes.object,
  processingUUID: PropTypes.string,
  onCheckStatus: PropTypes.func.isRequired,
};

const PatientLookupResult = ({ patientPending, processingUUID, onCheckStatus }) => {
  if (!patientPending.isPending) {
    return (
      <div style={{ padding: "12px", backgroundColor: "#f5f5f5", borderRadius: "4px" }}>
        <strong>{patientPending.patientGuid}</strong> has no pending Synthpop calls.{" "}
        <Link to={`/patients/${patientPending.patientGuid}`}>View patient</Link>
      </div>
    );
  }

  return (
    <div style={{ padding: "12px", backgroundColor: "#fff8e1", borderRadius: "4px" }}>
      <div style={{ marginBottom: "10px" }}>
        <strong>{patientPending.patientGuid}</strong> has {patientPending.pendingCalls?.length || 0} pending
        Synthpop {patientPending.pendingCalls?.length === 1 ? "call" : "calls"}.{" "}
        <Link to={`/patients/${patientPending.patientGuid}`}>View patient</Link>
      </div>
      <table className="table" style={{ width: "100%", borderCollapse: "collapse", margin: 0 }}>
        <thead>
          <tr>
            <th style={{ backgroundColor: "#fff" }}>Task Created</th>
            <th style={{ backgroundColor: "#fff" }}>Time Pending</th>
            <th style={{ backgroundColor: "#fff" }}>Attempt</th>
            <th style={{ backgroundColor: "#fff" }}>Synthpop Task</th>
            <th style={{ backgroundColor: "#fff" }}>UUID</th>
            <th style={{ backgroundColor: "#fff" }}>Check Status</th>
          </tr>
        </thead>
        <tbody>
          {(patientPending.pendingCalls || []).map(call => (
            <tr key={call.synthpopUUID}>
              <td>{formatDate(call.taskCreated)}</td>
              <td>{call.taskCreated ? moment(call.taskCreated).fromNow(true) : "-"}</td>
              <td>{call.isSecondaryAttempt ? "Secondary" : "Primary"}</td>
              <td>
                {call.synthpopTaskURL ? (
                  <a
                    href={call.synthpopTaskURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="View in Synthpop"
                    style={{ padding: "3px 8px" }}
                  >
                    <FaExternalLink />
                  </a>
                ) : (
                  <span style={{ color: "#999" }}>-</span>
                )}
              </td>
              <td>
                <span style={{ fontSize: "11px", fontFamily: "monospace", color: "#9c27b0" }}>
                  {call.synthpopUUID || "-"}
                </span>
              </td>
              <td>
                {call.synthpopUUID ? (
                  <HasRolePermission allowedRoles={["Administrator", "ServiceAdmin"]}>
                    <button
                      onClick={() => onCheckStatus(call.synthpopUUID)}
                      disabled={processingUUID === call.synthpopUUID}
                      title="Check Synthpop for call status"
                      style={{
                        padding: "3px 8px",
                        cursor: processingUUID === call.synthpopUUID ? "wait" : "pointer",
                        backgroundColor: "#ff9800",
                        color: "white",
                        border: "none",
                        borderRadius: "3px"
                      }}
                    >
                      {processingUUID === call.synthpopUUID ? "..." : <FaRefresh />}
                    </button>
                  </HasRolePermission>
                ) : (
                  <span style={{ color: "#999" }}>-</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

PatientLookupResult.propTypes = {
  patientPending: PropTypes.object.isRequired,
  processingUUID: PropTypes.string,
  onCheckStatus: PropTypes.func.isRequired,
};

export default SynthpopPendingCalls;
